import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QUIZMASTER_API } from '../config';
import { useAuth } from '../utils/AuthContext';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';

const limit = 5;

const normalizeDocument = (doc) => ({
    ...doc,
    filename: doc?.filename || 'Untitled Document',
    created_at: doc?.created_at || doc?.updated_at || '',
    updated_at: doc?.updated_at || doc?.created_at || '',
});

const LandingPage = ({ newDocument = null }) => {
    const navigate = useNavigate();
    const { authState, refreshAccessToken } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const userId = authState?.userId;
    const accessToken = authState?.accessToken;
    const supabaseRefreshToken = authState?.supabaseRefreshToken;

    const displayName = useMemo(() => {
        if (!authState?.userName) {
            return 'there';
        }
        const trimmedName = authState.userName.trim();
        if (!trimmedName) {
            return 'there';
        }
        const [firstName] = trimmedName.split(' ');
        return firstName || trimmedName;
    }, [authState?.userName]);

    const formatDate = useCallback(
        (value) =>
            formatToLocalDateTime(value, {
                fallback: 'N/A',
                includeSeconds: false,
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZoneName: null,
            }),
        []
    );

    const requestDocuments = useCallback(async (token) => {
        return axios.get(
            `${QUIZMASTER_API}/list-documents-by-user/?page=1&limit=${limit}`,
            {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            }
        );
    }, []);

    const handleResponseData = useCallback((data) => {
        if (!data || (Array.isArray(data) && data.length === 0)) {
            setDocuments([]);
            return;
        }

        const items = Array.isArray(data) ? data : data?.results || [];
        const normalized = items
            .map((doc) => normalizeDocument(doc))
            .sort((a, b) => {
                const bTime = getUtcTimestamp(b.updated_at) ?? 0;
                const aTime = getUtcTimestamp(a.updated_at) ?? 0;
                return bTime - aTime;
            })
            .slice(0, limit);
        setDocuments(normalized);
    }, []);

    const fetchRecentDocuments = useCallback(async () => {
        if (!userId) {
            setDocuments([]);
            setLoading(false);
            setError('');
            return;
        }

        setLoading(true);
        setError('');

        let tokenToUse = accessToken;

        try {
            if (!tokenToUse && supabaseRefreshToken) {
                tokenToUse = await refreshAccessToken();
            }

            if (!tokenToUse) {
                setDocuments([]);
                setLoading(false);
                return;
            }

            const response = await requestDocuments(tokenToUse);
            handleResponseData(response.data);
        } catch (err) {
            if (err.response?.status === 401 && supabaseRefreshToken) {
                try {
                    const refreshedToken = await refreshAccessToken();
                    if (!refreshedToken) {
                        throw new Error('Authentication failed. Please sign in again.');
                    }
                    const retryResponse = await requestDocuments(refreshedToken);
                    handleResponseData(retryResponse.data);
                } catch (refreshError) {
                    console.error('[LandingPage] Token refresh failed:', refreshError);
                    setDocuments([]);
                    setError('Authentication failed. Please sign in again.');
                }
            } else if ([204, 404].includes(err.response?.status)) {
                setDocuments([]);
            } else {
                console.error('[LandingPage] Failed to fetch documents:', err);
                setError('Failed to load your recent documents. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [userId, accessToken, supabaseRefreshToken, refreshAccessToken, requestDocuments, handleResponseData]);

    useEffect(() => {
        let isMounted = true;

        const runFetch = async () => {
            if (!isMounted) return;
            await fetchRecentDocuments();
        };

        runFetch();

        return () => {
            isMounted = false;
        };
    }, [fetchRecentDocuments]);

    useEffect(() => {
        if (!newDocument) return;

        setDocuments((prev) => {
            const incoming = normalizeDocument(newDocument);
            const withoutIncoming = prev.filter((doc) => doc.id !== incoming.id);
            return [incoming, ...withoutIncoming].slice(0, limit);
        });
    }, [newDocument]);

    const handleUploadClick = () => navigate('/upload');
    const handleViewAllClick = () => navigate('/documents');
    const handleDocumentClick = (doc) => {
        navigate('/quiz-list', { state: { selectedDocument: doc } });
    };

    const isNewUser = !loading && !error && documents.length === 0;

    return (
        <div className="home-landing">
            <section className="home-landing__hero">
                <div className="home-landing__hero-content">
                    <h1 className="home-landing__title">Welcome back, {displayName}!</h1>
                    <p className="home-landing__subtitle">
                        Your latest study materials are just a click away. Jump back into your quizzes or add something new to continue building your knowledge library.
                    </p>
                    <div className="home-landing__actions">
                        <button type="button" className="button" onClick={handleUploadClick}>
                            Upload Document
                        </button>
                        <button type="button" className="button button--secondary" onClick={handleViewAllClick}>
                            View Library
                        </button>
                    </div>
                </div>
            </section>

            <section className="home-landing__recent">
                <div className="home-landing__recent-header">
                    <h2 className="home-landing__recent-title">Recent Documents</h2>
                    {!loading && documents.length > 0 && (
                        <button type="button" className="button button--ghost" onClick={handleViewAllClick}>
                            See all documents
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="home-landing__state">Loading your documents...</div>
                ) : error ? (
                    <div className="home-landing__state home-landing__state--error">
                        <p>{error}</p>
                        <button type="button" className="button" onClick={fetchRecentDocuments}>
                            Retry
                        </button>
                    </div>
                ) : isNewUser ? (
                    <div className="home-landing__state">
                        <p>You haven&apos;t uploaded any documents yet.</p>
                        <button type="button" className="button" onClick={handleUploadClick}>
                            Upload your first document
                        </button>
                    </div>
                ) : (
                    <div className="home-landing__list">
                        {documents.map((doc) => (
                            <div key={doc.id} className="home-landing__card">
                                <div className="home-landing__card-main">
                                    <h3 className="home-landing__card-title">{doc.filename}</h3>
                                    <span className="home-landing__card-subtitle">Updated {formatDate(doc.updated_at)}</span>
                                </div>
                                <div className="home-landing__card-actions">
                                    <span className="home-landing__card-link" onClick={() => handleDocumentClick(doc)}>Open quiz</span>
                                    {doc.token_count > 0 && (
                                        <>
                                            <span className="home-landing__card-link" onClick={() => navigate('/flashcard-list', { state: { selectedDocument: doc } })}>Open flash card</span>
                                            <span className="home-landing__card-link" onClick={() => navigate('/podcast-list', { state: { selectedDocument: doc } })}>Open podcast</span>
                                            <span className="home-landing__card-link" onClick={() => navigate('/video-list', { state: { selectedDocument: doc } })}>Open video</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default LandingPage;